import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';

const IngredientLibrary = () => {
  const { user } = React.useContext(AuthContext);
  const [ingredients, setIngredients] = useState([]);
  const [filter, setFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    inci_name: '',
    type: 'oil',
    hlb_value: '',
    hlb_required: '',
    supplier: '',
    grade: '',
    code_mp: '',
    recommended_usage: '',
    character: '',
    ph_range: '',
    temperature_sensitivity: '',
    electrolyte_sensitivity: '',
    perfume_sensitivity: '',
    compatibility_notes: '',
  });

  useEffect(() => {
    fetchIngredients();
  }, [filter]);

  const fetchIngredients = async () => {
    try {
      const url = filter === 'all' ? `${API}/ingredients` : `${API}/ingredients?type=${filter}`;
      const response = await axios.get(url);
      setIngredients(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des ingrédients');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const submitData = { ...formData };
    if (submitData.hlb_value === '') submitData.hlb_value = null;
    if (submitData.hlb_required === '') submitData.hlb_required = null;

    try {
      if (editingIngredient) {
        await axios.put(`${API}/ingredients/${editingIngredient.id}`, submitData);
        toast.success('Ingrédient modifié avec succès !');
      } else {
        await axios.post(`${API}/ingredients`, submitData);
        toast.success('Ingrédient ajouté avec succès !');
      }

      setDialogOpen(false);
      resetForm();
      fetchIngredients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleEdit = (ingredient) => {
    setEditingIngredient(ingredient);
    setFormData({
      name: ingredient.name,
      inci_name: ingredient.inci_name,
      type: ingredient.type,
      hlb_value: ingredient.hlb_value || '',
      hlb_required: ingredient.hlb_required || '',
      supplier: ingredient.supplier || '',
      grade: ingredient.grade || '',
      code_mp: ingredient.code_mp || '',
      recommended_usage: ingredient.recommended_usage || '',
      character: ingredient.character || '',
      ph_range: ingredient.ph_range || '',
      temperature_sensitivity: ingredient.temperature_sensitivity || '',
      electrolyte_sensitivity: ingredient.electrolyte_sensitivity || '',
      perfume_sensitivity: ingredient.perfume_sensitivity || '',
      compatibility_notes: ingredient.compatibility_notes || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet ingrédient ?')) return;

    try {
      await axios.delete(`${API}/ingredients/${id}`);
      toast.success('Ingrédient supprimé');
      fetchIngredients();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setEditingIngredient(null);
    setFormData({
      name: '',
      inci_name: '',
      type: 'oil',
      hlb_value: '',
      hlb_required: '',
      supplier: '',
      grade: '',
      code_mp: '',
      recommended_usage: '',
      character: '',
      ph_range: '',
      temperature_sensitivity: '',
      electrolyte_sensitivity: '',
      perfume_sensitivity: '',
      compatibility_notes: '',
    });
  };

  const getTypeLabel = (type) => {
    const labels = {
      oil: 'Huile',
      emulsifier: 'Émulsionnant',
      butter: 'Beurre/Alcool',
      gum: 'Gomme',
    };
    return labels[type] || type;
  };

  const getTypeBadgeColor = (type) => {
    const colors = {
      oil: 'bg-[#2F4840]/10 text-[#2F4840]',
      emulsifier: 'bg-[#346A53]/10 text-[#346A53]',
      butter: 'bg-[#B94A3E]/10 text-[#B94A3E]',
      gum: 'bg-[#5C615E]/10 text-[#5C615E]',
    };
    return colors[type] || colors.oil;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-4xl font-semibold heading-font text-[#1A1C1B]" data-testid="ingredients-title">
            Bibliothèque d'Ingrédients
          </h2>
          <p className="text-[#5C615E] mt-2">
            Gérer les matières premières et leurs propriétés
          </p>
        </div>
        {user?.role === 'admin' && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[#2F4840] hover:bg-[#22352F] text-white" data-testid="add-ingredient-button">
                <Plus className="h-4 w-4 mr-2" />
                Ajouter un ingrédient
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingIngredient ? 'Modifier l\'ingrédient' : 'Nouvel ingrédient'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="ingredient-form">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nom *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="border-[#E5E5DF]"
                      data-testid="ingredient-name-input"
                    />
                  </div>
                  <div>
                    <Label>Nom INCI *</Label>
                    <Input
                      value={formData.inci_name}
                      onChange={(e) => setFormData({ ...formData, inci_name: e.target.value })}
                      required
                      className="border-[#E5E5DF]"
                      data-testid="ingredient-inci-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Type *</Label>
                    <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                      <SelectTrigger data-testid="ingredient-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="oil">Huile</SelectItem>
                        <SelectItem value="emulsifier">Émulsionnant</SelectItem>
                        <SelectItem value="butter">Beurre/Alcool</SelectItem>
                        <SelectItem value="gum">Gomme</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>HLB Valeur (émulsionnant)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.hlb_value}
                      onChange={(e) => setFormData({ ...formData, hlb_value: e.target.value })}
                      className="border-[#E5E5DF]"
                      data-testid="ingredient-hlb-value-input"
                    />
                  </div>
                  <div>
                    <Label>HLB Requis (huile/beurre)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.hlb_required}
                      onChange={(e) => setFormData({ ...formData, hlb_required: e.target.value })}
                      className="border-[#E5E5DF]"
                      data-testid="ingredient-hlb-required-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Fournisseur</Label>
                    <Input
                      value={formData.supplier}
                      onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                      className="border-[#E5E5DF]"
                    />
                  </div>
                  <div>
                    <Label>Grade</Label>
                    <Input
                      value={formData.grade}
                      onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                      className="border-[#E5E5DF]"
                    />
                  </div>
                  <div>
                    <Label>Code MP</Label>
                    <Input
                      value={formData.code_mp}
                      onChange={(e) => setFormData({ ...formData, code_mp: e.target.value })}
                      className="border-[#E5E5DF]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>% Conseille fournisseur</Label>
                    <Input
                      value={formData.recommended_usage}
                      onChange={(e) => setFormData({ ...formData, recommended_usage: e.target.value })}
                      placeholder="Ex: 2-5% ou 3%"
                      className="border-[#E5E5DF]"
                      data-testid="ingredient-recommended-usage-input"
                    />
                  </div>
                  <div>
                    <Label>Caractere (emulsionnants)</Label>
                    <Select
                      value={formData.character}
                      onValueChange={(value) => setFormData({ ...formData, character: value })}
                    >
                      <SelectTrigger data-testid="ingredient-character-select">
                        <SelectValue placeholder="Selectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">Non applicable</SelectItem>
                        <SelectItem value="hydrophile">Hydrophile</SelectItem>
                        <SelectItem value="lipophile">Lipophile</SelectItem>
                        <SelectItem value="amphotere">Amphotere</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Plage pH</Label>
                    <Input
                      value={formData.ph_range}
                      onChange={(e) => setFormData({ ...formData, ph_range: e.target.value })}
                      placeholder="Ex: 5.0-7.0"
                      className="border-[#E5E5DF]"
                    />
                  </div>
                  <div>
                    <Label>Sensibilité Température</Label>
                    <Select
                      value={formData.temperature_sensitivity}
                      onValueChange={(value) => setFormData({ ...formData, temperature_sensitivity: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Faible</SelectItem>
                        <SelectItem value="medium">Modérée</SelectItem>
                        <SelectItem value="high">Élevée</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Sensibilité Électrolytes</Label>
                    <Select
                      value={formData.electrolyte_sensitivity}
                      onValueChange={(value) => setFormData({ ...formData, electrolyte_sensitivity: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Faible</SelectItem>
                        <SelectItem value="medium">Modérée</SelectItem>
                        <SelectItem value="high">Élevée</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Sensibilité Parfums</Label>
                    <Select
                      value={formData.perfume_sensitivity}
                      onValueChange={(value) => setFormData({ ...formData, perfume_sensitivity: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Faible</SelectItem>
                        <SelectItem value="medium">Modérée</SelectItem>
                        <SelectItem value="high">Élevée</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Notes de compatibilité</Label>
                  <textarea
                    value={formData.compatibility_notes}
                    onChange={(e) => setFormData({ ...formData, compatibility_notes: e.target.value })}
                    className="w-full min-h-[80px] px-3 py-2 text-sm border border-[#E5E5DF] rounded-sm focus:outline-none focus:ring-2 focus:ring-[#9BBAB0]"
                    placeholder="Informations additionnelles sur la compatibilité..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    className="bg-[#2F4840] hover:bg-[#22352F] text-white"
                    data-testid="submit-ingredient-button"
                  >
                    {editingIngredient ? 'Modifier' : 'Créer'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setDialogOpen(false);
                      resetForm();
                    }}
                    className="border-[#E5E5DF]"
                  >
                    Annuler
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex gap-2">
        <Button
          variant={filter === 'all' ? 'default' : 'outline'}
          onClick={() => setFilter('all')}
          className={filter === 'all' ? 'bg-[#2F4840] text-white' : 'border-[#E5E5DF]'}
          data-testid="filter-all"
        >
          Tous ({ingredients.length})
        </Button>
        <Button
          variant={filter === 'emulsifier' ? 'default' : 'outline'}
          onClick={() => setFilter('emulsifier')}
          className={filter === 'emulsifier' ? 'bg-[#2F4840] text-white' : 'border-[#E5E5DF]'}
          data-testid="filter-emulsifier"
        >
          Émulsionnants
        </Button>
        <Button
          variant={filter === 'oil' ? 'default' : 'outline'}
          onClick={() => setFilter('oil')}
          className={filter === 'oil' ? 'bg-[#2F4840] text-white' : 'border-[#E5E5DF]'}
          data-testid="filter-oil"
        >
          Huiles
        </Button>
        <Button
          variant={filter === 'butter' ? 'default' : 'outline'}
          onClick={() => setFilter('butter')}
          className={filter === 'butter' ? 'bg-[#2F4840] text-white' : 'border-[#E5E5DF]'}
          data-testid="filter-butter"
        >
          Beurres/Alcools
        </Button>
      </div>

      <Card className="border-[#E5E5DF]">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full table-zebra">
              <thead>
                <tr className="bg-[#2F4840] text-white text-xs uppercase tracking-wide">
                  <th className="text-left p-3">Nom</th>
                  <th className="text-left p-3">INCI</th>
                  <th className="text-left p-3">Type</th>
                  <th className="text-right p-3">HLB Valeur</th>
                  <th className="text-right p-3">HLB Requis</th>
                  <th className="text-left p-3">% Conseille</th>
                  <th className="text-left p-3">Caractere</th>
                  <th className="text-left p-3">Fournisseur</th>
                  {user?.role === 'admin' && <th className="text-center p-3">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {ingredients.map((ing) => (
                  <tr key={ing.id} data-testid={`ingredient-row-${ing.id}`}>
                    <td className="p-3 font-medium text-[#1A1C1B]">{ing.name}</td>
                    <td className="p-3 text-sm text-[#5C615E]">{ing.inci_name}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-sm text-xs font-medium ${getTypeBadgeColor(ing.type)}`}>
                        {getTypeLabel(ing.type)}
                      </span>
                    </td>
                    <td className="p-3 text-right font-medium mono-font">
                      {ing.hlb_value !== null ? ing.hlb_value : '-'}
                    </td>
                    <td className="p-3 text-right font-medium mono-font">
                      {ing.hlb_required !== null ? ing.hlb_required : '-'}
                    </td>
                    <td className="p-3 text-sm text-[#1A1C1B]">
                      {ing.recommended_usage || '-'}
                    </td>
                    <td className="p-3 text-sm">
                      {ing.character ? (
                        <span className={`px-2 py-1 rounded-sm text-xs font-medium ${
                          ing.character === 'hydrophile' ? 'bg-blue-100 text-blue-700' :
                          ing.character === 'lipophile' ? 'bg-amber-100 text-amber-700' :
                          'bg-purple-100 text-purple-700'
                        }`}>
                          {ing.character.charAt(0).toUpperCase() + ing.character.slice(1)}
                        </span>
                      ) : '-'}
                    </td>
                    <td className="p-3 text-sm text-[#5C615E]">{ing.supplier || '-'}</td>
                    {user?.role === 'admin' && (
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(ing)}
                            className="border-[#E5E5DF] hover:bg-[#F6F7F6]"
                            data-testid={`edit-ingredient-${ing.id}`}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(ing.id)}
                            className="border-[#E5E5DF] hover:bg-[#F6F7F6] text-[#B94A3E]"
                            data-testid={`delete-ingredient-${ing.id}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default IngredientLibrary;
